import { useState } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Note from 'components/form/Note.component';
import Toggler from 'components/form/Toggler.component';
import TypedOption from 'components/form/TypedOption.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';

import CN from './TaxesAndFeesSection.module.scss';

const SHOW_MORE_THRESHOLD = 5;

export const TaxesAndFeesSectionView = ({
  taxes,
  fees,
  isEdit,
  location,
  onSelectTax,
  onSelectFee,
  onToggleExempt,
  onChangeReason,
  taxesByDefault,
  feesByDefault,
  isExemptByDefault,
  reasonByDefault,
}) => {
  const [selectedTaxes, setSelectedTaxes] = useState(taxesByDefault ?? {});
  const [selectedFees, setSelectedFees] = useState(feesByDefault ?? {});
  const [isExempt, setIsExempt] = useState(isExemptByDefault ?? false);
  const [reason, setReason] = useState(reasonByDefault ?? '');
  const [hasReasonError, setHasReasonError] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const { isSalonInUS } = useGilt();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  if (!isSalonInUS) return null;

  const isProductSection = location === 'product';
  const isServiceSection = location === 'service';

  const isEditWithoutSelection =
    isEdit && Object.keys(taxesByDefault).length === 0 && Object.keys(feesByDefault).length === 0 && Object.keys(selectedTaxes).length === 0 && Object.keys(selectedFees).length === 0;

  const filterByDefault = (rule) => {
    if (isEdit) {
      return rule;
    }
    return rule.is_default;
  };

  const filterByAppliedTo = (rule) => {
    if (isProductSection) {
      return rule.applied_to === 'products' || rule.applied_to === 'products_and_services';
    }
    if (isServiceSection) {
      return rule.applied_to === 'services' || rule.applied_to === 'products_and_services';
    }
    return rule;
  };

  const defaultTaxes = taxes.filter(filterByAppliedTo).filter(filterByDefault);
  const defaultFees = fees.filter(filterByAppliedTo).filter(filterByDefault);
  const hasDefaults = defaultTaxes.length > 0 || defaultFees.length > 0;

  const bannerMap = {
    product: t('composite.taxesAndFeesSection.exemptProductBanner'),
    service: t('composite.taxesAndFeesSection.exemptServiceBanner'),
  };

  const filterByShowMore = (rule) => {
    if ((!hasDefaults && !isEdit) || isEdit) {
      return rule;
    }
    return showMore ? rule : rule.is_default;
  };

  const sortedTaxes = taxes
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter(filterByAppliedTo)
    .filter(filterByShowMore);

  const sortedFees = fees
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter(filterByAppliedTo)
    .filter(filterByShowMore);

  const selectTax = (tax) => {
    const isSelected = selectedTaxes[tax.id] !== undefined ? !selectedTaxes[tax.id] : isEdit ? true : !tax.is_default;
    const newTaxes = { ...selectedTaxes, [tax.id]: isSelected };
    onSelectTax?.(newTaxes);
    setSelectedTaxes(newTaxes);
  };

  const selectFee = (fee) => {
    const isSelected = selectedFees[fee.id] !== undefined ? !selectedFees[fee.id] : isEdit ? true : !fee.is_default;
    const newFees = { ...selectedFees, [fee.id]: isSelected };
    onSelectFee?.(newFees);
    setSelectedFees(newFees);
  };

  const toggleExempt = () => {
    const newIsExempt = !isExempt;
    onToggleExempt?.(newIsExempt);
    setIsExempt(newIsExempt);
    if (!newIsExempt) {
      onChangeReason?.('');
      setReason('');
      setHasReasonError(false);
    } else if (!reason.trim()) {
      setHasReasonError(true);
    }
  };

  const validateReason = (event) => {
    const value = event.target.value;
    if (!value.trim()) {
      setHasReasonError(true);
    } else {
      setHasReasonError(false);
    }
  };

  const handleReasonChange = ({ raw }) => {
    const value = raw ?? '';
    setReason(value);
    onChangeReason(value);
    if (value.trim()) {
      setHasReasonError(false);
    }
  };

  const toggleMore = () => {
    setShowMore(!showMore);
  };

  const totalRules = sortedTaxes.length + sortedFees.length;
  const shouldShowMoreButton = totalRules > SHOW_MORE_THRESHOLD;

  return (
    <div className={isProductSection ? 'col-sm-7' : ''}>
      <div className={CN.taxesAndFeesSection}>
        <h3 className={cn(CN.title, isDarkMode && isServiceSection && CN.dark)} data-bem="TaxesAndFeesSection__title">
          {t('composite.taxesAndFeesSection.title')}
        </h3>
        {isSalonInUS && (
          <div className={CN.exemptToggle}>
            <label className={CN.exemptToggleLabel} htmlFor="isExempt">
              {t('composite.taxesAndFeesSection.exemptToggle')}
            </label>
            <Toggler tag="TaxesAndFeesSection__toggler" selected={isExempt} onClick={toggleExempt} hideLabel />
          </div>
        )}
      </div>
      {isExempt && (
        <div className={CN.exemptBanner}>
          <AlertPanel variant="alert" size="sm" tag="TaxesAndFeesSection__banner">
            <Icon variant="alert" width="md" />
            <span className={CN.exemptBannerText}>{bannerMap[location]}</span>
          </AlertPanel>

          <label htmlFor="exemptionReason">
            {t('composite.taxesAndFeesSection.exemptionReason')} <span className={CN.required}>*</span>
          </label>
          <Note name="exemptionReason" tag="TaxesAndFeesSection__exemptionReason" value={reason} onChange={handleReasonChange} onBlur={validateReason} />
          <span className={CN.exemptBannerSubtext}>{t('composite.taxesAndFeesSection.exemptionReasonSubtext')}</span>
          {hasReasonError && <span className={CN.errorMessage}>{t('composite.taxesAndFeesSection.errorMessage')}</span>}
        </div>
      )}
      <label htmlFor="assignedTaxesAndFees">{t('composite.taxesAndFeesSection.assignedTaxesAndFees')}</label>
      <div className={CN.rulePanelContainer}>
        {isSalonInUS &&
          sortedTaxes.map((tax) => {
            const editSelected = isEditWithoutSelection
              ? false
              : selectedTaxes?.[tax.id] !== undefined
                ? selectedTaxes?.[tax.id]
                : taxesByDefault?.[tax.id] !== undefined
                  ? taxesByDefault?.[tax.id]
                  : false;
            const newSelected = selectedTaxes[tax.id] ?? tax.is_default;
            const isSelected = isEdit ? editSelected : newSelected;
            return (
              // FIXME: inconsistent theming pattern with other components, should be refactored in the future
              <div className={cn(CN.rulePanel, isDarkMode && isServiceSection && CN.dark)}>
                <TypedOption key={`tax-${tax.id}`} type="simple-checkbox" name={tax?.name ?? ''} checked={isSelected} onCheck={() => selectTax(tax)} disabled={isExempt} />
                <div className={CN.rulePanelTextContainer}>
                  <span className={cn(CN.rulePanelText, isDarkMode && isServiceSection && CN.dark)}>{tax.name}</span>
                  <span className={cn(CN.rulePanelText, isDarkMode && isServiceSection && CN.dark)}>
                    {tax.amount && <CurrencySign value={tax.amount} />}
                    {tax.percentage && <span>{tax.percentage}%</span>}
                  </span>
                </div>
              </div>
            );
          })}
        {sortedFees.map((fee) => {
          const editSelected = isEditWithoutSelection ? false : selectedFees?.[fee.id] !== undefined ? selectedFees?.[fee.id] : feesByDefault?.[fee.id] !== undefined ? feesByDefault?.[fee.id] : false;
          const newSelected = selectedFees?.[fee.id] ?? fee.is_default;
          const isSelected = isEdit ? editSelected : newSelected;
          return (
            <div className={cn(CN.rulePanel, isDarkMode && isServiceSection && CN.dark)}>
              <TypedOption key={`fee-${fee.id}`} type="simple-checkbox" name={fee?.name ?? ''} checked={isSelected} onCheck={() => selectFee(fee)} />
              <div className={CN.rulePanelTextContainer}>
                <span className={cn(CN.rulePanelText, isDarkMode && isServiceSection && CN.dark)}>{fee.name}</span>
                <span className={cn(CN.rulePanelText, isDarkMode && isServiceSection && CN.dark)}>
                  {fee.amount && <CurrencySign value={fee.amount} />}
                  {fee.percentage && <span>{fee.percentage}%</span>}
                </span>
              </div>
            </div>
          );
        })}
        {!isEdit && shouldShowMoreButton && (
          <div className={CN.showMoreButtonContainer}>
            <Button variant="secondary" onClick={toggleMore} width="small">
              <div className={CN.showMoreButtonContent}>
                <Icon variant={showMore ? 'minus' : 'plus'} width="lg" />
                <span className={CN.showMoreText}>{showMore ? t('composite.taxesAndFeesSection.showLess') : t('composite.taxesAndFeesSection.showMore')}</span>
              </div>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const TaxesAndFeesSection = ({
  isEdit,
  isLoading,
  taxes,
  fees,
  location,
  onSelectTax,
  onSelectFee,
  onToggleExempt,
  onChangeReason,
  taxesByDefault,
  feesByDefault,
  isExemptByDefault,
  reasonByDefault,
}) => {
  if (isLoading) return <InlineSpinner />;

  return (
    <TaxesAndFeesSectionView
      isEdit={isEdit}
      taxes={taxes}
      fees={fees}
      location={location}
      onSelectTax={onSelectTax}
      onSelectFee={onSelectFee}
      onToggleExempt={onToggleExempt}
      onChangeReason={onChangeReason}
      taxesByDefault={taxesByDefault}
      feesByDefault={feesByDefault}
      isExemptByDefault={isExemptByDefault}
      reasonByDefault={reasonByDefault}
    />
  );
};
